import { useContext, useEffect } from "react";
import stateContext from "../../Context/StateContext";
import { diamondData } from "./DiamondData";
import "./AddDotDiamond.css";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const AddDiamond = () => {
    const globalState = useContext(stateContext);
    const diamonds = Object.entries(diamondData);

    useEffect(() => {
        if (globalState.selectedFontData?.attachmentSize?.sizes?.length) {
            const sizes = globalState.selectedFontData.attachmentSize.sizes;

            // Extract only numeric part
            const numericSizes = sizes.map((size) =>
                parseInt(size.replace(/\D/g, ""), 10)
            );

            if (numericSizes.length > 0) {
                globalState.setActiveDiamondSize(numericSizes[0]);
            }
        }
    }, [globalState.selectedFontData]);

    // Set initial dotDiamondData on mount
    useEffect(() => {
        if (diamonds.length > 0) {
            const [key, data] = diamonds[0];
            const current = globalState.dotDiamondData;

            if (!current || current.name !== key) {
                globalState.setDotDiamondData({ name: key, ...data });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Default diamond type when panel opens
    useEffect(() => {
        if (globalState.addDotDiamond && diamonds.length > 0) {
            const [firstKey] = diamonds[0];
            if (!globalState.diamondType) {
                globalState.setDiamondType(firstKey);
                let price =
                    globalState.attachmentPricing[0].sizes[
                        `${globalState.activeDiamondSize}mm`
                    ].price;

                if (price) {
                    globalState.setDiamondPrice(price);
                }
            }
        }
    }, [globalState.addDotDiamond, diamonds, globalState]);

    const handleAddDiamondsClick = () => {
        const newAddDotDiamondState = !globalState.addDotDiamond;
        globalState.setAddDotDiamond(newAddDotDiamondState);
        
        // Check if mobile view and toggle fullscreen
        const isMobile = window.innerWidth <= 1367;
        if (isMobile && newAddDotDiamondState) {
            globalState.setIsMobileFullscreen(true);
        }
    };

    const handleCloseFullscreen = () => {
        globalState.setIsMobileFullscreen(false);
        globalState.setAddDotDiamond(false);
    };

    const handleSelectType = (key) => {
        globalState.setDiamondType(key);

        // Find the selected item from attachmentPricing
        const selectedItem = globalState.attachmentPricing?.find(
            (item) => item.fontType === key
        );

        if (selectedItem) {
            // Get the active diamond size
            const activeSizeRaw = globalState.activeDiamondSize;
            const activeSizeNum =
                activeSizeRaw != null ? parseFloat(activeSizeRaw) : NaN;

            // Helper function to find price for active size
            const findPriceForActiveSize = (sizes, activeNum) => {
                if (!sizes || Number.isNaN(activeNum)) return null;

                // If sizes is an object with keys like "2", "2.0", "2mm"
                if (!Array.isArray(sizes) && typeof sizes === "object") {
                    // try direct string keys
                    const directKeysToTry = [
                        `${activeNum}`,
                        `${activeNum.toFixed(1)}`,
                        `${activeNum}mm`,
                        `${activeNum} mm`,
                    ];
                    for (const k of directKeysToTry) {
                        if (sizes[k] !== undefined) {
                            const v = sizes[k];
                            return typeof v === "object"
                                ? v.price ?? v.value ?? null
                                : v;
                        }
                    }

                    // try numeric-match by stripping non-digits from keys
                    for (const [k, v] of Object.entries(sizes)) {
                        const kNum = parseFloat(
                            String(k).replace(/[^\d.]/g, "")
                        );
                        if (
                            !Number.isNaN(kNum) &&
                            Math.abs(kNum - activeNum) < 1e-6
                        ) {
                            return typeof v === "object"
                                ? v.price ?? v.value ?? null
                                : v;
                        }
                    }
                }

                // If sizes is array, try to find an item with matching size property
                if (Array.isArray(sizes)) {
                    for (const el of sizes) {
                        if (typeof el === "number") {
                            // array of numbers — ambiguous, assume these are size values not prices
                            if (Math.abs(el - activeNum) < 1e-6) return null;
                        } else if (el && typeof el === "object") {
                            // look for common fields: size, mm, diameter, label
                            const sizeVal =
                                el.size ?? el.mm ?? el.diameter ?? el.label;
                            const priceVal = el.price ?? el.value ?? el.amount;
                            if (sizeVal != null) {
                                const sizeNum = parseFloat(
                                    String(sizeVal).replace(/[^\d.]/g, "")
                                );
                                if (
                                    !Number.isNaN(sizeNum) &&
                                    Math.abs(sizeNum - activeNum) < 1e-6
                                ) {
                                    return typeof priceVal === "number"
                                        ? priceVal
                                        : null;
                                }
                            }
                        }
                    }
                }

                return null;
            };

            // Helper function to get fallback price
            const getPriceFromSizesFallback = (sizes) => {
                if (!sizes) return 0;
                if (Array.isArray(sizes) && sizes.length > 0) {
                    const first = sizes[0];
                    if (typeof first === "number") return first;
                    if (first && typeof first.price === "number")
                        return first.price;
                }
                if (typeof sizes === "object") {
                    const entries = Object.entries(sizes);
                    if (entries.length === 0) return 0;
                    let numericPrices = [];
                    for (const [, v] of entries) {
                        if (typeof v === "number") numericPrices.push(v);
                        else if (v && typeof v.price === "number")
                            numericPrices.push(v.price);
                    }
                    if (numericPrices.length > 0)
                        return Math.min(...numericPrices);
                    const [, val0] = entries[0];
                    if (typeof val0 === "number") return val0;
                    if (val0 && typeof val0.price === "number")
                        return val0.price;
                }
                return 0;
            };

            // Calculate the price for the selected diamond type
            const activePrice = findPriceForActiveSize(
                selectedItem.sizes,
                activeSizeNum
            );
            const price =
                activePrice ??
                getPriceFromSizesFallback(selectedItem.sizes) ??
                0;

            // Set the diamond price in global state
            globalState.setDiamondPrice(price);
        }

        // Update dot diamond data (existing logic)
        const selectedData = diamondData[key];
        if (selectedData) {
            globalState.setDotDiamondData({ name: key, ...selectedData });
        }
    };

    const handleRemoveDiamond = (indexToRemove) => {
        const updatedData = globalState.diamondPosData.filter(
            (_, index) => index !== indexToRemove
        );

        globalState.setDiamondPosData(updatedData);

        if (
            globalState.updatingDiamondIndex === indexToRemove ||
            globalState.updatingDiamondIndex >= updatedData.length
        ) {
            globalState.setUpdatingDiamondIndex(null);
            globalState.setHoveredDiamondIndex(-1);
        }
    };

    useEffect(() => {
        globalState.setDotDiamondPrice(
            globalState.diamondPosData.length * globalState.diamondPrice
        );
        globalState.setNoOfDotDiamond(globalState.diamondPosData.length);
        
        // If diamonds were added and we're in mobile fullscreen, show close button
        // The close functionality will be handled in MainCanvas
        if (globalState.diamondPosData.length > 0 && globalState.isMobileFullscreen) {
            // Diamond added in fullscreen mode - close button will be shown by MainCanvas
        }
    }, [globalState.diamondPosData, globalState]);

    // Handle window resize to close fullscreen if switching from mobile to desktop
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth <= 1367;
            if (!isMobile && globalState.isMobileFullscreen) {
                globalState.setIsMobileFullscreen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [globalState]);

    return (
        <div className="tabpanel__content">
            <div className="dot-diamond">
                {!globalState.addDotDiamond && (
                    <div
                        className="add-diamond-button"
                        onClick={handleAddDiamondsClick}
                    >
                        <Tooltip
                            title={
                                globalState.selectedFontLanguage === "Arabic"
                                    ? "أضف الماس"
                                    : "Add Diamond"
                            }
                            placement="right"
                            arrow
                        >
                            <button className="add-diamond-btn">
                                <AddIcon className="add-icon" />
                            </button>
                        </Tooltip>
                    </div>
                )}

                <>
                    {globalState.addDotDiamond && (
                        <div className="diamond-add-instruction">
                            {globalState.selectedFontLanguage === "Arabic"
                                ? globalState.isMobileFullscreen 
                                    ? "انقر على النص ثلاثي الأبعاد لإضافة الماسة. ستظهر أيقونة الإغلاق بعد إضافة الماسة"
                                    : "انقر على النص ثلاثي الأبعاد لإضافة الماسة"
                                : globalState.isMobileFullscreen 
                                    ? "Click on the 3D Text to Add a Diamond. Close button will appear after adding diamond"
                                    : "Click on the 3D Text to Add a Diamond"}
                        </div>
                    )}
                    <div className="diamond-type-panel">
                        <div className="diamond-name">
                            {globalState.selectedFontLanguage === "Arabic"
                                ? "اختر نوع الماس"
                                : "Choose diamond type"}
                        </div>

                        <div className="diamond-type-options">
                            {(globalState.attachmentPricing || []).map(
                                (item) => {
                                    const key = item.fontType || "unknown";
                                    const imageSrc = `${
                                        process.env.PUBLIC_URL || ""
                                    }${item.imgUrl || ""}`;

                                    // Active size from global state (could be number or string)
                                    const activeSizeRaw =
                                        globalState.activeDiamondSize;
                                    const activeSizeNum =
                                        activeSizeRaw != null
                                            ? parseFloat(activeSizeRaw)
                                            : NaN;

                                    // helpers
                                    const getPriceFromSizesFallback = (
                                        sizes
                                    ) => {
                                        if (!sizes) return 0;
                                        if (
                                            Array.isArray(sizes) &&
                                            sizes.length > 0
                                        ) {
                                            const first = sizes[0];
                                            if (typeof first === "number")
                                                return first;
                                            if (
                                                first &&
                                                typeof first.price === "number"
                                            )
                                                return first.price;
                                        }
                                        if (typeof sizes === "object") {
                                            const entries =
                                                Object.entries(sizes);
                                            if (entries.length === 0) return 0;
                                            let numericPrices = [];
                                            for (const [, v] of entries) {
                                                if (typeof v === "number")
                                                    numericPrices.push(v);
                                                else if (
                                                    v &&
                                                    typeof v.price === "number"
                                                )
                                                    numericPrices.push(v.price);
                                            }
                                            if (numericPrices.length > 0)
                                                return Math.min(
                                                    ...numericPrices
                                                );
                                            const [, val0] = entries[0];
                                            if (typeof val0 === "number")
                                                return val0;
                                            if (
                                                val0 &&
                                                typeof val0.price === "number"
                                            )
                                                return val0.price;
                                        }
                                        return 0;
                                    };

                                    const findPriceForActiveSize = (
                                        sizes,
                                        activeNum
                                    ) => {
                                        if (!sizes || Number.isNaN(activeNum))
                                            return null;

                                        // If sizes is an object with keys like "2", "2.0", "2mm"
                                        if (
                                            !Array.isArray(sizes) &&
                                            typeof sizes === "object"
                                        ) {
                                            // try direct string keys
                                            const directKeysToTry = [
                                                `${activeNum}`,
                                                `${activeNum.toFixed(1)}`,
                                                `${activeNum}mm`,
                                                `${activeNum} mm`,
                                            ];
                                            for (const k of directKeysToTry) {
                                                if (sizes[k] !== undefined) {
                                                    const v = sizes[k];
                                                    return typeof v === "object"
                                                        ? v.price ??
                                                              v.value ??
                                                              null
                                                        : v;
                                                }
                                            }

                                            // try numeric-match by stripping non-digits from keys
                                            for (const [k, v] of Object.entries(
                                                sizes
                                            )) {
                                                const kNum = parseFloat(
                                                    String(k).replace(
                                                        /[^\d.]/g,
                                                        ""
                                                    )
                                                );
                                                if (
                                                    !Number.isNaN(kNum) &&
                                                    Math.abs(kNum - activeNum) <
                                                        1e-6
                                                ) {
                                                    return typeof v === "object"
                                                        ? v.price ??
                                                              v.value ??
                                                              null
                                                        : v;
                                                }
                                            }
                                        }

                                        // If sizes is array, try to find an item with matching size property
                                        if (Array.isArray(sizes)) {
                                            for (const el of sizes) {
                                                if (typeof el === "number") {
                                                    // array of numbers — ambiguous, assume these are size values not prices
                                                    if (
                                                        Math.abs(
                                                            el - activeNum
                                                        ) < 1e-6
                                                    )
                                                        return null; // can't determine price from plain numbers
                                                } else if (
                                                    el &&
                                                    typeof el === "object"
                                                ) {
                                                    // look for common fields: size, mm, diameter, label
                                                    const sizeVal =
                                                        el.size ??
                                                        el.mm ??
                                                        el.diameter ??
                                                        el.label;
                                                    const priceVal =
                                                        el.price ??
                                                        el.value ??
                                                        el.amount;
                                                    if (sizeVal != null) {
                                                        const sizeNum =
                                                            parseFloat(
                                                                String(
                                                                    sizeVal
                                                                ).replace(
                                                                    /[^\d.]/g,
                                                                    ""
                                                                )
                                                            );
                                                        if (
                                                            !Number.isNaN(
                                                                sizeNum
                                                            ) &&
                                                            Math.abs(
                                                                sizeNum -
                                                                    activeNum
                                                            ) < 1e-6
                                                        ) {
                                                            return typeof priceVal ===
                                                                "number"
                                                                ? priceVal
                                                                : null;
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        // nothing found for active size
                                        return null;
                                    };

                                    const activePrice = findPriceForActiveSize(
                                        item.sizes,
                                        activeSizeNum
                                    );
                                    const price =
                                        activePrice ??
                                        getPriceFromSizesFallback(item.sizes) ??
                                        0;

                                    const isActive =
                                        globalState.diamondType === key;

                                    const formatPrice = (p) => {
                                        if (p == null) return "N/A";
                                        // show integer if whole, otherwise one decimal
                                        const v = Number(p);
                                        if (Number.isNaN(v)) return "N/A";
                                        return Number.isInteger(v)
                                            ? v.toString()
                                            : v.toFixed(1);
                                    };

                                    return (
                                        <button
                                            key={key}
                                            className={`diamond-type-option ${
                                                isActive ? "active" : ""
                                            }`}
                                            onClick={() =>
                                                handleSelectType(key)
                                            }
                                            type="button"
                                        >
                                            <img
                                                src={imageSrc}
                                                alt={key}
                                                className="diamond-type-image"
                                                onError={(e) => {
                                                    e.currentTarget.onerror =
                                                        null;
                                                    e.currentTarget.src = `${
                                                        process.env
                                                            .PUBLIC_URL || ""
                                                    }/assets/images/placeholder.png`;
                                                }}
                                            />
                                            <div className="diamond-type-label">
                                                <div className="diamond-type-name">
                                                    {key}
                                                </div>

                                                {/* size line */}
                                                {/* {displaySize ? (
                                                    <div className="diamond-type-size">
                                                        {displaySize}
                                                    </div>
                                                ) : (
                                                    <div className="diamond-type-size">
                                                        —
                                                    </div>
                                                )} */}

                                                {/* price line */}
                                                <div className="diamond-type-price">
                                                    {globalState.selectedFontLanguage ===
                                                    "Arabic"
                                                        ? `${formatPrice(
                                                              price
                                                          )} SAR`
                                                        : `SAR ${formatPrice(
                                                              price
                                                          )}`}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </>

                {globalState.diamondPosData.length > 0 && (
                    <div>
                        <h4 className="added-diamonds-title">
                            {globalState.selectedFontLanguage === "Arabic"
                                ? "قائمة الماسات المضافة"
                                : "Added Diamonds List"}{" "}
                        </h4>
                        <div className="diamond-pos-list">
                            {globalState.diamondPosData.map(
                                (diamond, index) => (
                                    <div
                                        key={index}
                                        className="diamond-pos-item"
                                        onMouseEnter={() =>
                                            globalState.setHoveredDiamondIndex(
                                                index
                                            )
                                        }
                                        onMouseLeave={() =>
                                            globalState.setHoveredDiamondIndex(
                                                -1
                                            )
                                        }
                                        onClick={() => {
                                            globalState.setUpdatingDiamondIndex(
                                                index
                                            );
                                        }}
                                    >
                                        <div className="diamond-list-left">
                                            <p className="diamond-name">
                                                {globalState.selectedFontLanguage ===
                                                "Arabic"
                                                    ? `ألماسة ${index + 1}`
                                                    : `Diamond ${
                                                          index + 1
                                                      }`}{" "}
                                            </p>
                                            <div className="diamond-list-meta">
                                                <span className="diamond-type">
                                                    {diamond.type ||
                                                        diamond.name}
                                                </span>
                                                <span className="diamond-price">
                                                    {globalState.selectedFontLanguage ===
                                                    "Arabic"
                                                        ? `${globalState.diamondPrice} ريال`
                                                        : `SAR ${globalState.diamondPrice}`}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            className="remove-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveDiamond(index);
                                            }}
                                        >
                                            <CloseIcon className="remove-icon" />
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddDiamond;
